'use client';

import { useAnalyticsSkuViewedContext } from '@cc-shopping/platform-analytics/AnalyticsSkuViewed/Context';
import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import { CTASearchParams } from '@cc-shopping/platform-analytics/lib/ctaSearchParams';
import { FinishType, type LinkType } from '@cc-shopping/platform-analytics/types';
import { AccessibleHiddenText } from '@cc-shopping/ui/primitives/AccessibleHiddenText';
import { spacing } from '@cc-shopping/ui/theme';
import { omit } from '@cc-shopping/utils/object/omit';
import { Box, type BoxProps, Link, type SxProps, type Theme, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { type CtaAnalyticsProps, CtaLink } from '../CTA/CtaLink';
import { RatesAndFeesLink } from '../RatesAndFeesLink';

export const PrimaryLink = styled(Link)(({ theme }) => {
  const { common, primary } = theme.palette;
  return {
    alignItems: 'center',
    appearance: 'button',
    backgroundColor: primary.main,
    borderRadius: '0.125rem',
    color: primary.contrastText,
    display: 'flex',
    fontFamily: theme.typography.fontFamily,
    fontSize: '.8125rem',
    fontWeight: 700,
    gap: '0.5rem',
    justifyContent: 'center',
    letterSpacing: '.0625em',
    minWidth: '150px',
    padding: '0.8rem 0',
    textDecoration: 'none',
    textTransform: 'uppercase',
    touchAction: 'manipulation',
    transition: 'background-color .15s ease-out',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'none',
      backgroundColor: primary.dark,
      color: primary.contrastText,
    },
    '&:active': {
      backgroundColor: `${primary.main} !important;`,
    },
    '&:focus': {
      color: primary.contrastText,
    },
    '&:visited': {
      color: common.white,
    },
  };
});

export const SecondaryLink = styled(Link)(({ theme }) => {
  const { common, primary } = theme.palette;
  return {
    alignItems: 'center',
    appearance: 'button',
    backgroundColor: common.white,
    borderRadius: '0.125rem',
    color: primary.main,
    border: `1px solid ${primary.main}`,
    display: 'flex',
    fontFamily: theme.typography.fontFamily,
    fontSize: '.8125rem',
    fontWeight: 700,
    gap: '0.5rem',
    justifyContent: 'center',
    letterSpacing: '.0625em',
    minWidth: '150px',
    padding: '0.8rem 0',
    textDecoration: 'none',
    textTransform: 'uppercase',
    touchAction: 'manipulation',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'none',
      outline: `1px solid ${primary.main}`,
    },
    '&:visited': {
      color: primary.main,
    },
  };
});

export const ProjectGreenLink = styled(PrimaryLink)(({ theme }) => {
  return {
    color: theme.palette.lime.main,
    boxShadow:
      '0px 4px 8px 0px rgba(100, 102, 106, 0.10), 0px 1px 4px 0px rgba(100, 102, 106, 0.36);',
    '&:hover': {
      color: theme.palette.lime.main,
      background: theme.palette.greens.dark,
    },
    '&:focus': {
      color: theme.palette.lime.main,
    },
    '&:visited': {
      color: theme.palette.lime.main,
    },
  };
});

const NewWindowIcon = () => (
  <span
    aria-hidden="true"
    className="mask-icon mask-icon-open-in-new"
    style={{
      width: '1rem',
      height: '1rem',
    }}
  />
);

const PhoneNumberLink = styled('a')(({ theme }) => ({
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  touchAction: 'manipulation',
  color: theme.palette.secondary.main,
  '&:hover': {
    textDecoration: 'underline',
  },
  '&:focus': {
    borderRadius: '4px',
  },
}));

export const ApplyNowButton = ({
  id,
  applyLink,
  applyPhoneNumber,
  institutionName,
  isActive,
  isMonetizing,
  reviewLink,
  termsFeesRedirectLink,
  hideIcon,
  linkSx,
  analytics,
  ctaType,
  versionId,
  overrideShoppingFinishedId,
  hideOnMobile = false,
  hideBelowContent = false,
  buttonTextOverride,
  bottomTextOverride,
  variant = 'primary',
  device,
  isSpanFullWidth = false,
  name,
  ...props
}: ApplyNowButtonProps) => {
  let link = applyLink;
  let text = buttonTextOverride ?? 'Apply Now';
  let isReview = false;
  const { skuImpressionId } = useAnalyticsSkuViewedContext();
  const fireRatesAndFeesPageAction = usePageAction({
    actionName: 'rates_and_fees',
    actionSection: analytics.section?.name,
    actionType: 'open',
    additionalAttributes: {
      sku_impression_id: skuImpressionId,
    },
  });

  if (!isActive || !isMonetizing || !applyLink) {
    link = reviewLink;
    text = 'Read Review';
    isReview = true;
    hideIcon = true;
  }

  const sectionName = analytics.section?.name || 'default';
  const phoneNumberId = device
    ? `${device}-${id}-${sectionName}-phone-number-link`
    : `${id}-${sectionName}-phone-number-link`;

  if (!link) return null;

  const issuerName = /s$/.test(institutionName) ? `${institutionName}'` : `${institutionName}'s`;
  const website = institutionName?.toLowerCase() === 'citibank' ? 'application' : 'website';
  const phoneText = applyPhoneNumber ? 'or call ' : '';

  const bottomText =
    bottomTextOverride ?? `on ${issuerName} ${website}${applyPhoneNumber ? ',' : ''}`;

  const CTALinkComponent = {
    projectGreen: ProjectGreenLink,
    primary: PrimaryLink,
  }[variant];

  return (
    <Box
      id={id}
      {...omit(props, ['innerRef'])}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(2),
        width: isSpanFullWidth ? '100%' : 'auto',
        ...props.sx,
      }}
    >
      <CtaLink
        analytics={{
          ...analytics,
          linkType: isReview ? undefined : 'APPLY_NOW_BUTTON',
        }}
        type={ctaType ?? 'APPLY_NOW_BUTTON'}
        href={link}
        Component={CTALinkComponent}
        rel={isReview ? undefined : 'nofollow'}
        target={isReview ? '_self' : '_blank'}
        sx={linkSx}
        finishType={isReview ? FinishType.InternalContent : FinishType.ExternalApplication}
        isReview={isReview}
        parameterActions={
          isReview ? [] : [CTASearchParams.MONETIZING, CTASearchParams.SHOPPING_FINISH]
        }
        shoppingFinishedId={overrideShoppingFinishedId}
      >
        {text}
        {!hideIcon && <NewWindowIcon />}
        {!isReview && !hideBelowContent && (
          <AccessibleHiddenText>{bottomText}</AccessibleHiddenText>
        )}
      </CtaLink>
      {!isReview && !hideBelowContent && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignSelf: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="bodySmall"
              align="center"
              fontSize={{ xs: '13px', md: '12px' }}
              sx={theme => ({
                color: theme.palette.grey[700],
              })}
            >
              {bottomText}
            </Typography>
            <Typography
              id={phoneNumberId}
              variant="bodySmall"
              align="center"
              fontSize={{ xs: '13px', md: '12px' }}
              sx={theme => ({
                color: theme.palette.grey[700],
              })}
            >
              {phoneText && <>&nbsp;{phoneText}</>}
              {applyPhoneNumber && (
                <PhoneNumberLink href={`tel:${applyPhoneNumber}`} aria-labelledby={phoneNumberId}>
                  {applyPhoneNumber}
                </PhoneNumberLink>
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              textAlign: 'center',
            }}
          >
            {termsFeesRedirectLink && (
              <Box
                sx={theme => ({
                  [theme.breakpoints.down('md')]: {
                    display: hideOnMobile ? 'none' : 'block',
                  },
                })}
              >
                <RatesAndFeesLink
                  analytics={analytics}
                  href={termsFeesRedirectLink}
                  versionId={versionId}
                  onClickAnalyticsCallback={() => {
                    fireRatesAndFeesPageAction();
                  }}
                  containerSxProps={{
                    fontSize: { xs: '13px', md: '12px' },
                  }}
                  name={name}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
export interface ApplyNowButtonProps extends Partial<BoxProps> {
  id: string;
  applyLink?: string;
  applyPhoneNumber?: string;
  institutionName: string;
  isActive?: boolean;
  isMonetizing?: boolean;
  reviewLink?: string;
  termsFeesRedirectLink?: string;
  hideIcon?: boolean;
  linkSx?: SxProps<Theme>;
  analytics: CtaAnalyticsProps;
  ctaType?: LinkType;
  versionId: number;
  hideOnMobile?: boolean;
  overrideShoppingFinishedId?: string;
  hideBelowContent?: boolean;
  buttonTextOverride?: string;
  bottomTextOverride?: string;
  variant?: 'projectGreen' | 'primary';
  device?: 'mobile' | 'desktop';
  isSpanFullWidth?: boolean;
  name: string;
}
